import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Paper,
  Divider,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Grid
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import { useForm } from '../../hooks';
import { useAuth } from '../../contexts/AuthContext';

const SignUp = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  
  const steps = ['Account Information', 'School Information'];
  
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    submitError,
    setFieldValue,
    validateAllFields
  } = useForm(
    {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      schoolName: '',
      schoolType: '',
      schoolAddress: '',
      schoolCity: '',
      schoolState: '',
      schoolZip: '',
      schoolPhone: ''
    },
    ['firstName', 'lastName', 'email', 'password', 'confirmPassword', 'schoolName', 'schoolType'],
    async (formData) => {
      // Call register function from auth context
      await register(formData);
      
      // Navigate to school profile creation
      navigate('/create-school', { 
        state: { 
          schoolInfo: {
            name: formData.schoolName,
            type: formData.schoolType,
            address: formData.schoolAddress,
            city: formData.schoolCity,
            state: formData.schoolState,
            zip: formData.schoolZip,
            phone: formData.schoolPhone
          }
        } 
      });
    },
    'signup'
  );
  
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  
  const toggleShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };
  
  const handleNext = () => {
    // Validate current step fields
    const fieldsToValidate = activeStep === 0 
      ? ['firstName', 'lastName', 'email', 'password', 'confirmPassword']
      : ['schoolName', 'schoolType'];
    
    const stepErrors = {};
    fieldsToValidate.forEach(field => {
      if (!values[field]) {
        stepErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')} is required`;
      }
    });
    
    // Check if passwords match
    if (activeStep === 0 && values.password !== values.confirmPassword) {
      stepErrors.confirmPassword = 'Passwords do not match';
    }
    
    // If there are errors, don't proceed
    if (Object.keys(stepErrors).length > 0) {
      // Mark fields as touched to show errors
      const touchedFields = {};
      fieldsToValidate.forEach(field => {
        touchedFields[field] = true;
      });
      
      // Update errors state
      Object.keys(stepErrors).forEach(field => {
        errors[field] = stepErrors[field];
        touched[field] = true;
      });
      
      return;
    }
    
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 2
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src="/logo192.png"
              alt="SafeLine Logo"
              sx={{
                width: 60,
                height: 60,
                mb: 2
              }}
            />
            
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              Create Your Account
            </Typography>
            
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
            >
              Sign up for a SafeLine admin account
            </Typography>
          </Box>
          
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {/* Error message */}
          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {submitError}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            {activeStep === 0 ? (
              // Step 1: Account Information
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="firstName"
                      name="firstName"
                      label="First Name"
                      variant="outlined"
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="lastName"
                      name="lastName"
                      label="Last Name"
                      variant="outlined"
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.lastName && Boolean(errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
                
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email Address"
                  variant="outlined"
                  margin="normal"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  margin="normal"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={toggleShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  fullWidth
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  variant="outlined"
                  margin="normal"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={toggleShowConfirmPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            ) : (
              // Step 2: School Information
              <>
                <TextField
                  fullWidth
                  id="schoolName"
                  name="schoolName"
                  label="School Name"
                  variant="outlined"
                  margin="normal"
                  value={values.schoolName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.schoolName && Boolean(errors.schoolName)}
                  helperText={touched.schoolName && errors.schoolName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SchoolIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  fullWidth
                  id="schoolType"
                  name="schoolType"
                  label="School Type"
                  variant="outlined"
                  margin="normal"
                  value={values.schoolType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.schoolType && Boolean(errors.schoolType)}
                  helperText={touched.schoolType && errors.schoolType}
                  select
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value=""></option>
                  <option value="elementary">Elementary School</option>
                  <option value="middle">Middle School</option>
                  <option value="high">High School</option>
                  <option value="k12">K-12 School</option>
                  <option value="private">Private School</option>
                  <option value="charter">Charter School</option>
                  <option value="other">Other</option>
                </TextField>
                
                <TextField
                  fullWidth
                  id="schoolAddress"
                  name="schoolAddress"
                  label="School Address"
                  variant="outlined"
                  margin="normal"
                  value={values.schoolAddress}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.schoolAddress && Boolean(errors.schoolAddress)}
                  helperText={touched.schoolAddress && errors.schoolAddress}
                />
                
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="schoolCity"
                      name="schoolCity"
                      label="City"
                      variant="outlined"
                      value={values.schoolCity}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.schoolCity && Boolean(errors.schoolCity)}
                      helperText={touched.schoolCity && errors.schoolCity}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      id="schoolState"
                      name="schoolState"
                      label="State"
                      variant="outlined"
                      value={values.schoolState}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.schoolState && Boolean(errors.schoolState)}
                      helperText={touched.schoolState && errors.schoolState}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      id="schoolZip"
                      name="schoolZip"
                      label="ZIP Code"
                      variant="outlined"
                      value={values.schoolZip}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.schoolZip && Boolean(errors.schoolZip)}
                      helperText={touched.schoolZip && errors.schoolZip}
                    />
                  </Grid>
                </Grid>
                
                <TextField
                  fullWidth
                  id="schoolPhone"
                  name="schoolPhone"
                  label="School Phone"
                  variant="outlined"
                  margin="normal"
                  value={values.schoolPhone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.schoolPhone && Boolean(errors.schoolPhone)}
                  helperText={touched.schoolPhone && errors.schoolPhone}
                />
              </>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Back
              </Button>
              
              {activeStep === steps.length - 1 ? (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  sx={{ position: 'relative' }}
                >
                  {isSubmitting ? (
                    <CircularProgress
                      size={24}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-12px',
                        marginLeft: '-12px',
                      }}
                    />
                  ) : (
                    'Create Account'
                  )}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </Box>
          </form>
          
          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Already have an account?
            </Typography>
            
            <Button
              component={RouterLink}
              to="/login"
              variant="outlined"
              color="primary"
              fullWidth
              sx={{ mt: 1 }}
            >
              Sign In
            </Button>
          </Box>
        </Paper>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          &copy; {new Date().getFullYear()} SafeLine. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
};

export default SignUp;
